import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

import renderAccordionParagraphs from '@utils/renderAccordionParagraphs'

export const faqsList: Record<
    CreditCardProductCategory,
    { heading: string; text: React.ReactElement }[]
> = {
    ['BalanceTransfer']: [
        {
            heading: 'How do balance transfer credit cards work?',
            text: renderAccordionParagraphs([
                'A balance transfer lets you move existing credit card debt to a new provider who let’s you pay 0% interest for a set period of time. Just remember that you’ll need to pay a one-off transfer fee (usually 2-4% of the amount transferred). However, the savings can be considerable as you can often stop paying interest for months or even years, potentially saving you hundreds, if not thousands of pounds.',
            ]),
        },

        {
            heading: 'How much can I save with a balance transfer?',
            text: renderAccordionParagraphs([
                'For those with excellent credit scores and carrying the average interest-bearing balances, the savings can thousands of pounds. It all depends on how much you owe, and how good your credit score is. So remember to shop around, and look out for pre-approved and guaranteed offers.',
            ]),
        },

        {
            heading: 'What fees do balance transfer cards charge?',
            text: renderAccordionParagraphs([
                'Most balance transfer cards charge a fee of 2-4% of the amount you transfer. For example, if you transfer £3,000 with a 3.5% fee, you’d pay £105 upfront. While this might seem like a lot, it’s usually much less than the interest you’d save over time.',
            ]),
        },

        {
            heading: 'Will I be accepted for a balance transfer card?',
            text: renderAccordionParagraphs([
                'This depends on your credit score and your personal financial situation. The best deals are usually reserved for borrowers with good to excellent credit scores. However, there are options available for those with less than perfect credit too.',
                'Always check your eligibility first to avoid damaging your credit score with unnecessary applications.',
            ]),
        },

        {
            heading: 'How long do 0% balance transfer offers last?',
            text: renderAccordionParagraphs([
                'The longest balance transfers at the moment last for more than 30 months. However, these are usually only available to those with the best credit scores. If yours is less than perfect, you might be eligible for a balance transfer of at least 9 months, which can still save you a considerable amount of money.',
            ]),
        },

        {
            heading: 'Can I use a balance transfer card for purchases?',
            text: renderAccordionParagraphs([
                'It is possible to make purchases with a balance transfer, but watch out because the rates are usually high. So, it’s best to use these cards for transferring and paying down existing debt. If you need to make purchases, look for a 0% purchase card, or a combo card which gives you 0% on what you buy and transfers.',
            ]),
        },

        {
            heading: 'What happens when the 0% period ends?',
            text: renderAccordionParagraphs([
                'Once the initial offer ends, you’ll start paying the card’s standard APR. That’s why it’s so important to make a plan to clear your balance before the 0% period ends, or be ready to move to another 0% deal if you need some more space.',
            ]),
        },

        {
            heading: 'Should I transfer my entire balance?',
            text: renderAccordionParagraphs([
                'If possible, yes. Moving your full balance will mean you can stop paying all interest on that debt. However, make sure the new card’s credit limit is high enough. TotallySure guarantees the APR, offer duration, and credit limit, giving you extra certainty when applying.',
            ]),
        },

        {
            heading: 'How many times can I do a balance transfer?',
            text: renderAccordionParagraphs([
                'There’s no limit, but with each transfer you’ll usually pay a fee, and multiple applications can affect your credit score. It’s better to find a card with a long enough 0% period to clear your debt, and stick to the plan, rather than constantly moving balances around.',
            ]),
        },

        {
            heading: 'What should I avoid when using a balance transfer card?',
            text: renderAccordionParagraphs([
                'Avoid using your card for buying things unless it has a separate 0% purchase offer. You should also set up a Direct Debit so you can avoid missing payments, as this could end your promotional rate early.',
            ]),
        },
    ],

    ['BestCards']: [
        {
            heading: 'How do credit cards work?',
            text: renderAccordionParagraphs([
                'Credit cards let you borrow money to make purchases. Each month you get a statement showing what you owe, and if you pay the full balance you won’t be charged interest.',
            ]),
        },

        {
            heading:
                'What’s the difference between a credit card and a debit card?',
            text: renderAccordionParagraphs([
                'A credit card lets you borrow money that you pay back later whereas a debit card lets you spend money directly from your bank account.',
            ]),
        },

        {
            heading: 'What is APR?',
            text: renderAccordionParagraphs([
                'APR stands for Annual Percentage Rate and shows you how much it costs to borrow for a year, including interest and fees. The higher the APR, the more expensive it can be to borrow.',
            ]),
        },

        {
            heading: 'Will I be accepted for a credit card?',
            text: renderAccordionParagraphs([
                'This depends on things like your credit history and income. Lenders will look at whether you can afford to repay what you borrow, and how well you’ve managed it in the past. It’s a good idea to check your eligibility before applying so you can see your chances of being accepted.',
            ]),
        },

        {
            heading: 'What is a credit limit?',
            text: renderAccordionParagraphs([
                'This is the maximum amount you can borrow on your card. Your credit limit often depends on your income and borrowing history, and going over the limit will usually result in additional fees.',
            ]),
        },

        {
            heading: 'What’s a minimum payment?',
            text: renderAccordionParagraphs([
                'It’s the smallest amount you must repay each month to avoid any fees. It’s usually a percentage of your balance. Remember that paying the minimum can let interest build up, taking longer to clear your debt.',
            ]),
        },

        {
            heading: 'Do I get Section 75 protection?',
            text: renderAccordionParagraphs([
                'Any eligible purchase between £100 and £30,000 made on a credit card is protected. If something goes wrong with what you bought, your card provider shares responsibility with the seller.',
            ]),
        },

        {
            heading: 'Will using a credit card affect my credit score?',
            text: renderAccordionParagraphs([
                'Making payments on time and not using too much of your limit can help to improve your score. Missing payments or going over your limit is likely to damage it.',
            ]),
        },

        {
            heading: 'Can I have more than one credit card?',
            text: renderAccordionParagraphs([
                'Yes, but this can make managing them more difficult. Having multiple cards can help your credit score if you use them well – but it also means more payments to track, and could increase the temptation to overspend.',
            ]),
        },

        {
            heading: 'What should I avoid with credit cards?',
            text: renderAccordionParagraphs([
                'Avoid only making minimum payments as interest can start to add up, and try not to miss payments as this could damage your credit score. Using a credit card to withdraw cash can result in added fees and leave a mark on your credit file. Finally, don’t spend more than you can afford to repay.',
            ]),
        },
    ],

    ['Cashback']: [
        {
            heading: 'How do cashback credit cards work?',
            text: renderAccordionParagraphs([
                'When you buy something with a cashback card, you earn a percentage back on what you’ve spent. This could be a flat rate, or different rates for different types of spending.',
                'Cashback is usually paid monthly or annually, and can either be a credit on your statement or sent directly to your account.',
            ]),
        },

        {
            heading: 'What’s the difference between cashback and rewards?',
            text: renderAccordionParagraphs([
                'Cashback gives real money back. Rewards on the other hand are usually points that you can exchange for specific things, such as vouchers or air miles. And that means cashback is usually simpler and more flexible.',
            ]),
        },

        {
            heading: 'How much cashback can I earn?',
            text: renderAccordionParagraphs([
                'This depends on the card and how much you spend. Different providers will offer different offers, and most will cap how much you can earn annually. So always remember to check the terms.',
            ]),
        },

        {
            heading: 'Do cashback cards have annual fees?',
            text: renderAccordionParagraphs([
                'Basic cashback cards are often free, while premium ones might charge you to use them. Before applying for a card with a fee, work out if you’ll earn enough in cashback to cover the cost.',
            ]),
        },

        {
            heading: 'Are cashback credit cards worth it?',
            text: renderAccordionParagraphs([
                'If you use them smartly and clear the balance every month, cashback cards can be worth it. If you don’t, the interest you pay can quickly cost more than the cashback you earn.',
            ]),
        },

        {
            heading: 'Do I get Section 75 protection?',
            text: renderAccordionParagraphs([
                'Like all credit cards, eligible purchases between £100 and £30,000 on a cashback card are covered by Section 75 of the Consumer Credit Act. This means your card provider shares legal responsibility with the seller if something goes wrong.',
            ]),
        },

        {
            heading: 'Will I be accepted for a cashback card?',
            text: renderAccordionParagraphs([
                'Your chances of being accepted will depend on your credit history. Just remember that the best offers will usually only be available to people with stronger credit scores. Check your credit report before you apply to make sure the information is correct and up to date, and start taking the right steps to building your score and unlocking the best offers.',
            ]),
        },

        {
            heading: 'Is there a limit to how much cashback I can earn?',
            text: renderAccordionParagraphs([
                'Most providers will cap how much you can earn per year, and this could be a fixed amount, or based on how much you spend. Make sure you read the small print before applying to avoid any surprises.',
            ]),
        },

        {
            heading: 'Can I use my cashback card abroad?',
            text: renderAccordionParagraphs([
                'If you travel a lot, check to see if your provider charges you for using your card abroad, as these fees can eat into your cashback.',
            ]),
        },

        {
            heading: 'What should I avoid with cashback cards?',
            text: renderAccordionParagraphs([
                'Avoid overspending to earn cashback, as you might end up carrying a balance – and that could cost you more than the money you earn. Also check for annual fees and work out if they’re worth it, and don’t forget to factor in lower rates after introductory periods end.',
            ]),
        },
    ],

    ['Combined']: [
        {
            heading: 'How do balance transfer and purchase cards work?',
            text: renderAccordionParagraphs([
                'These cards give you 0% interest on both balance transfers and new purchases, helping you cut the cost of borrowing. For a one-off fee, you can transfer your existing debt from other lenders, and make new purchases without paying any interest. The 0% periods are usually different lengths, so set a reminder for when each one ends.',
            ]),
        },

        {
            heading:
                'What’s the difference between combo cards and specialist cards?',
            text: renderAccordionParagraphs([
                'Instead of giving you a 0% offer on just transfers or purchases, you get both. But the durations are usually shorter than what you would get on a specialist card. So shop around and find the best option for your own personal circumstances.',
            ]),
        },

        {
            heading: 'How much does it cost to transfer a balance?',
            text: renderAccordionParagraphs([
                'Like specialist balance transfers, you’ll be charged a fee of around 2-4% of the amount you move.',
            ]),
        },

        {
            heading: 'Are the 0% periods the same for transfers and purchases?',
            text: renderAccordionParagraphs([
                'They’re usually different, so before you apply check all your options and find the best card for your own needs.',
            ]),
        },

        {
            heading: 'Do I get Section 75 protection?',
            text: renderAccordionParagraphs([
                'Yes you will, on any eligible purchase between £100 and £30,000. That means your card provider shares legal responsibility with the seller if something goes wrong.',
            ]),
        },

        {
            heading: 'Will I be accepted for a combo card?',
            text: renderAccordionParagraphs([
                'Your chances of being accepted depends on your credit history, and how well lenders think you can manage what you borrow. The best offers with the longest 0% periods will usually only be available to people with the best credit scores – so remember to check that all the information is correct on your credit report before you apply.',
                'Once you’ve the right product, check your eligibility to find out your chances of acceptance, and look out TotallySure offers which come with pre-approval and guaranteed terms.',
            ]),
        },

        {
            heading: 'How long do I have to request a balance transfer?',
            text: renderAccordionParagraphs([
                'Most cards will give you 60-90 days to transfer the balance from the date you opened your account. After that, you might not be able to transfer, or you might find yourself paying a higher rate.',
            ]),
        },

        {
            heading:
                'Can I transfer balances from cards with the same banking group?',
            text: renderAccordionParagraphs([
                'Most banking groups won’t let you transfer balances between cards they issue. So, before you apply make sure you check to see if it’s possible, otherwise you might need to find a different provider for the balance transfer.',
            ]),
        },

        {
            heading: 'What happens when the 0% periods end?',
            text: renderAccordionParagraphs([
                'When each promotional period ends, you’ll start paying the interest on what you owe – and that’s why it’s important to try to clear balances before the end of the 0% period.',
            ]),
        },

        {
            heading: 'Should I focus on clearing transfers or purchases first?',
            text: renderAccordionParagraphs([
                'Your card will automatically pay off the balance with the higher interest rate first – and once the 0% period ends on either part, that will become the priority.',
            ]),
        },

        {
            heading: 'What should I avoid with combo cards?',
            text: renderAccordionParagraphs([
                'With any credit card, it’s important to avoid overspending. Keep track of both promotional end dates, and try to clear the balances within the 0% periods.',
            ]),
        },
    ],

    ['PoorCredit']: [
        {
            heading: 'Can I get a credit card with bad credit?',
            text: renderAccordionParagraphs([
                'Yes, you can still get a credit card if you have a poor bad credit history. There are several specialist lenders who help people rebuild their credit scores, and this includes those with defaults, previous rejections, thin credit files, and CCJs.',
                'What’s important is that you find the right card for your own personal situation. Always check your eligibility before you apply, as this will give you an idea of how likely you are to be accepted, without impacting your credit file.',
            ]),
        },

        {
            heading: 'How do I know if I’ll be accepted for a credit card?',
            text: renderAccordionParagraphs([
                'Before applying, use an eligibility checker, as this will tell you your chances of being accepted without leaving a mark on your credit file.',
                'At TotallyMoney, we go one step further, and show offers which come with pre-approval, and a guaranteed APR, credit limit, and length of offer. That you, you’ll know if you’ll be accepted, and that what you apply for is exactly what you’ll get. We call these offers ‘TotallySure’. Just look out for the badge when searching for an offer.',
            ]),
        },

        {
            heading:
                'What’s the difference between credit building cards and normal credit cards?',
            text: renderAccordionParagraphs([
                'Credit building cards are also known as credit cards for poor credit, or bad credit credit cards. They’re designed to help people with poor credit scores, or damaged files rebuild their profile, so they can access better offers, and even loans and mortgages.',
                'Usually, they come with a lower credit limit of between £200-£1,500, a higher interest rate, shorter terms, and fewer perks. But they are easier to get accepted, and if you use them properly, you can start building your score – and once you’ve done that, you can apply for cards with lower rates, longer terms, and bigger limits.',
            ]),
        },

        {
            heading: 'Will a credit card help improve my credit score?',
            text: renderAccordionParagraphs([
                'If used properly, it can. A good practice is to make smaller, day-to-day purchases (like your shopping or commute), and to pay off the balance in full each month. You should also aim to use less than 30% of your credit limit, and set up a Direct Debit so you never miss a payment.',
                'If you stick to the plan, you should see an improvement in your score within 6-12 months.',
            ]),
        },

        {
            heading:
                'What should I avoid when using a credit card for bad credit?',
            text: renderAccordionParagraphs([
                'It’s important to use the card responsibly. That means keeping the amount of available credit you’re using low, never missing payments, avoiding cash withdrawals, and avoiding multiple applications.',
                'All these things can leave a negative mark on your credit file, and lower your score.',
            ]),
        },

        {
            heading: 'How much will a credit card for bad credit cost me?',
            text: renderAccordionParagraphs([
                'This depends on how poor your credit score is, and what you’re eligible for.',
                'Rates can vary between 20% and 60%, but it’s worth remembering that if you pay off your card every month, in full, you’ll never pay any interest. So treat it as a tool, rather than a way of borrowing money.',
            ]),
        },

        {
            heading: 'What credit limit will I get with bad credit?',
            text: renderAccordionParagraphs([
                'Credit cards for poor credit scores come with lower limits as you’ll need to rebuild trust with lenders before they’ll let you borrow more. Usually, limits will range from £200 and up to £1,500, but this depends on your circumstances and the lender.',
                'When deciding what limit to set, they’ll look at your income, debts, and previous history. As you start showing good financial behaviour, you’ll find that the lender might offer to extend the limit, or offer you a more competitive product.',
            ]),
        },

        {
            heading: 'How long will it take to improve my credit score?',
            text: renderAccordionParagraphs([
                'This depends on your credit history, and how you use the new card. For people with a thin file, this could take as little as 3-6 months, but those with poor credit might take up to two years.',
                'You’ll just need to keep consistent, and show the lender that you’re good at managing your credit – always repay on time, keep the balance low, and avoid applying for other credit cards.',
            ]),
        },

        {
            heading: 'What if I’m refused for a credit card for bad credit?',
            text: renderAccordionParagraphs([
                'If you’ve been rejected, it’s important that you avoid applying for another card straight away. That’s because multiple rejections in a short space of time can damage your credit score further.',
                'Check your credit report for errors, and raise a dispute if you see something that isn’t right. Then it might be a good idea to wait for a few months before making another application.',
                'Download the TotallyMoney app to see where you stand. We’ll give you a personalised plan to show you what’s holding you back, and how to move forward.',
            ]),
        },
    ],

    ['Purchase']: [
        {
            heading: 'How do 0% purchase credit cards work?',
            text: renderAccordionParagraphs([
                'When you make a purchase with a purchase credit card, you won’t pay any interest on what you spend during the 0% promotional period. Always remember that you still need to make at least the minimum monthly payment, but every penny you pay back goes towards paying off the debt, and not on interest charges.',
                'Once the 0% period ends, you’ll start paying interest on any remaining balance, so try to clear your debt before then.',
            ]),
        },

        {
            heading: 'How much can I save with a 0% purchase card?',
            text: renderAccordionParagraphs([
                'How much you can save depends on how much you spend, your credit score, and what you would otherwise be paying interest on. Just remember to try and pay off what you borrow before the 0% period ends, otherwise you’ll start paying interest on what you owe.',
            ]),
        },

        {
            heading: 'What fees do 0% purchase cards charge?',
            text: renderAccordionParagraphs([
                'Most 0% purchase cards won’t charge upfront fees. However, some might come with annual fees, cash withdrawal charges, or foreign transaction fees. So, always read the small print before applying.',
            ]),
        },

        {
            heading: 'Will I be accepted for a 0% purchase card?',
            text: renderAccordionParagraphs([
                'This depends on your credit score and the card you’re applying for. The longest 0% periods will usually only be given to borrowers with the best credit scores. However, shorter options are available for those who might not have a perfect credit history.',
                'Always check your eligibility before applying so you know your chances of being accepted. Keep an eye out for offers which come with pre-approval, guaranteed limits, APRs and offer lengths. We call these TotallySure, as you’ll be totally sure of being accepted, and that the offer you applied for is the one you’ll get.',
            ]),
        },

        {
            heading: 'How long do 0% purchase offers last? ',
            text: renderAccordionParagraphs([
                '0% purchase periods will vary between different banks and depending on your credit profile. They’ll also change throughout the year as banks compete for customers.',
            ]),
        },

        {
            heading: 'Can I use a 0% purchase card for balance transfers?',
            text: renderAccordionParagraphs([
                'Some, but not all cards offer 0% on both purchases and balance transfers, and when using a comparison site, you might be able to filter down to the specific kind of offer you need. These cards can often be a bit more complicated to manage, so do your research before applying.',
            ]),
        },

        {
            heading: 'What happens when the 0% period ends?',
            text: renderAccordionParagraphs([
                'Once the promotional period ends, you’ll start paying interest on the balance that’s left over. That’s why it’s important to try your best to clear the balance before the offer ends. Another option could be to shift what you owe to a 0% balance transfer card.',
            ]),
        },

        {
            heading: 'Should I pay off my 0% purchase card early?',
            text: renderAccordionParagraphs([
                'If you have other debts that you’re paying interest on, then it usually makes sense to pay them off first. However, if you have spare cash and no other debts, then paying off what you owe early can give you peace of mind, and frees up the credit for future use.',
            ]),
        },

        {
            heading: 'How many 0% purchase cards can I have?',
            text: renderAccordionParagraphs([
                'There’s no set limit to how many you can have, but remember that making multiple applications can affect your credit score. Trying to manage multiple cards with different promotional periods and different lenders can also be complicated. So, it’s usually better to find the best card for your own circumstances, and to use that.',
            ]),
        },

        {
            heading: 'What should I avoid when using a 0% purchase card?',
            text: renderAccordionParagraphs([
                'It’s important to remember that even though you’re not paying interest, the money isn’t free. You’ll still need to pay it back before the end of the 0% period, and that it can be hard to get out of debt if you have to start paying interest. Always avoid cash withdrawals, as you’ll usually pay more interest and extra fees. Try and make more than the minimum payment each month, and if possible, clear it in full before the end of the promotional period.',
            ]),
        },
    ],

    ['Rewards']: [
        {
            heading: 'How do rewards credit cards work?',
            text: renderAccordionParagraphs([
                'When you spend with a rewards card, you get something back – usually cashback as a percentage of what you spend, or points that you can spend at certain retailers. You’ll just need to try and repay the full balance every month, as these cards often come with higher APRs and no special interest-free periods. And that means if you don’t keep up with repayments, the interest you incur is likely to cost you more than the rewards you earn.',
            ]),
        },

        {
            heading: 'What’s the difference between cashback and points?',
            text: renderAccordionParagraphs([
                'Cashback cards give you money back – usually as credit on your statement or paid directly into your account. Points on the other hand need to be exchanged for specific rewards like shopping vouchers, air miles, and other experiences. Cashback is simpler and more flexible, but points may offer better value used smartly.',
            ]),
        },

        {
            heading: 'Are rewards credit cards worth it?',
            text: renderAccordionParagraphs([
                'Rewards cards can be worth it if you choose the best one to suit your lifestyle, and you clear your balance in full every month. That way, you’ll be earning on what you’d usually be spending anyway at no extra cost.',
            ]),
        },

        {
            heading: 'Do rewards credit cards have annual fees?',
            text: renderAccordionParagraphs([
                'Some do and some don’t. Standard rewards cards are often free, while premium ones with better perks are likely to come with an annual fee. Before taking out a card with a fee, do the calculations, and work out if you’ll be earning enough in rewards to justify the added cost.',
            ]),
        },

        {
            heading: 'Do I get Section 75 protection with rewards cards?',
            text: renderAccordionParagraphs([
                'Yes. Like all credit cards, rewards cards come with Section 75 protection on eligible purchases between £100 and £30,000. This means your card provider shares legal responsibility with the seller if something goes wrong.',
            ]),
        },

        {
            heading: 'Will I be accepted for a rewards card?',
            text: renderAccordionParagraphs([
                'Your chances of being accepted for credit product will usually come down to your credit history. Cards with the best rewards usually go to people with stronger credit profiles. But there may be options for people with not so perfect credit too – it’s just likely that they’ll have lower cashback rates or fewer perks.',
                'Make sure to check your eligibility before applying so you know your chances of being accepted. And look out for TotallySure offers which come with pre-approval and guaranteed terms.',
            ]),
        },

        {
            heading: 'What happens if I don’t pay off my balance in full?',
            text: renderAccordionParagraphs([
                'If you don’t pay off your balance in full each month, it’s likely that you’ll start paying interest on what you owe. And you could quickly end up paying more in interest than you’re earning in rewards.',
            ]),
        },

        {
            heading: 'Do reward points expire?',
            text: renderAccordionParagraphs([
                'Many points-based schemes have expiry dates – and they can be as short as a year or two. Cashback usually doesn’t expire, which is another reason it’s often simpler. Always read the small print so you don’t lose rewards you’ve earned.',
            ]),
        },

        {
            heading: 'Can I use my rewards card abroad?',
            text: renderAccordionParagraphs([
                'You can, but watch out for transaction fees on ATM withdrawals and spending. The costs can quickly add up, so you might be better off using your debit card, or getting a specialist travel card.',
            ]),
        },

        {
            heading: 'Should I get multiple rewards cards?',
            text: renderAccordionParagraphs([
                'Managing several cards with different rewards schemes, payment dates, and terms can be complicated, and you might find yourself missing payments or building up debt. Plus, making multiple applications in a short amount of time can impact your credit file. So, it might make more sense to find the card that best matches your spending habits and stick with it.',
            ]),
        },

        {
            heading: 'What should I avoid with rewards cards?',
            text: renderAccordionParagraphs([
                'It’s important to avoid carrying a balance and paying interest as this is likely to cost you far more than the value of any rewards. That means you should also avoid overspending to earn more points. If your card comes with high annual fees, work out the value you’re getting from the rewards to make sure you’re getting value for money.',
            ]),
        },
    ],

    ['Travel']: [
        {
            heading: 'How do travel credit cards work?',
            text: renderAccordionParagraphs([
                'Travel cards let you spend abroad and avoid paying transaction fees. They usually give you the card provider’s exchange rate, which is often better than what you’d pay with a regular card. Just remember to clear your balance when you get back to avoid interest charges.',
            ]),
        },

        {
            heading:
                'What’s the difference between travel cards and regular cards?',
            text: renderAccordionParagraphs([
                'Regular cards charge usually charge a percentage in foreign transaction fees and give you worse exchange rates. Travel cards don’t have these fees and offer better rates, which can save you a lot when you’re abroad.',
            ]),
        },

        {
            heading: 'Do I need to tell my card provider I’m travelling?',
            text: renderAccordionParagraphs([
                'It’s best to let them know in advance where you’re going and when, otherwise the bank might think the foreign transactions are fraudulent and block your card.',
            ]),
        },

        {
            heading: 'Should I pay in pounds or local currency?',
            text: renderAccordionParagraphs([
                'It’s almost always best to choose local currency. If you choose to pay in pounds, the shop or ATM can set the exchange rate, and they could add a big mark-up, making the transaction more expensive than it should be.',
            ]),
        },

        {
            heading: 'Can I withdraw cash with a travel card?',
            text: renderAccordionParagraphs([
                'Some cards let you withdraw cash fee-free, but you could start paying interest immediately. It’s often better to just use the card for purchases, and to use a fee-free debit card to withdraw cash.',
            ]),
        },

        {
            heading: 'Do I get Section 75 protection?',
            text: renderAccordionParagraphs([
                'With all credit cards you get Section 75 on purchases between £100 and £30,000. And this can be useful for holiday bookings, giving you added protection if something goes wrong.',
            ]),
        },

        {
            heading: 'Will I be accepted for a travel card?',
            text: renderAccordionParagraphs([
                'This depends on your credit history. Check your eligibility so you know your chances of being accepted before you apply. TotallySure offers come with pre-approval and guaranteed terms, giving you extra certainty.',
            ]),
        },

        {
            heading: 'Are there annual fees?',
            text: renderAccordionParagraphs([
                'Some travel cards are free, while more premium ones might charge a yearly fee. Work out if you’ll save enough on foreign fees to justify any annual costs.',
            ]),
        },

        {
            heading: 'Can I use my travel card in the UK?',
            text: renderAccordionParagraphs([
                'You can, but travel cards might have higher interest rates and no special offers for UK spending.',
            ]),
        },

        {
            heading: 'What should I avoid with travel cards?',
            text: renderAccordionParagraphs([
                'Avoid carrying a balance as the interest is likely to cost you more than you saved on fees, and try not to withdraw cash as interest applies immediately.',
            ]),
        },
    ],
}
